/**
 * Entry point. The runtime (src/app/runtime.ts) picks the data source: the real API in a
 * production build, the in-memory mock in development and in the static preview. URL
 * options for the mock (development and preview only):
 *   ?mock=still    frozen clock, no scripted agent steps
 *   ?mock=offline  no stream, so the stale state (approvals disabled) shows
 *   ?api=live      development only: the real API through the dev server's /api proxy
 *   ?auth=login    the mock starts signed out, to see the login screen (ADR 0012)
 *   ?theme=dark|light  force a theme for this page load without saving it
 *
 * The panel sits behind its own login (ADR 0012): AuthGate asks GET /api/v1/auth/session and
 * shows the login screen or starts the store. A 401 on any request, or a dropped live stream
 * while the server says signed out, ends the session on screen and keeps the address.
 */

import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-ext-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-ext-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-ext-600.css';
import '@fontsource/ibm-plex-sans-condensed/latin-600.css';
import '@fontsource/ibm-plex-sans-condensed/latin-ext-600.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-ext-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/ibm-plex-mono/latin-ext-500.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/screens.css';
import './styles/desktop.css';
import './styles/settings.css';
import './styles/leads.css';
import './styles/prospects.css';
import './styles/org.css';
import './styles/office.css';
import './styles/api-usage.css';
import './styles/site-stats.css';
import './styles/channels.css';
import './styles/site-chats.css';
import './styles/seo-report.css';
import './styles/ozet.css';
import './styles/sales-map.css';
import './styles/outreach-log.css';
import './styles/auth.css';
import './styles/motion.css';

import { API_BASE_PATH } from '@twin/contracts';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { type AuthApi, createHttpAuthApi } from './api/auth.js';
import type { TwinApi } from './api/client.js';
import { createHttpApi } from './api/http.js';
import { createEventSourceLive, type LiveEvents } from './api/live.js';
import { AuthGate, createSessionWatch, type SessionWatch, watchingFetch } from './app/auth.js';
import { setRouterMode } from './app/router.js';
import { chooseRuntime, PREVIEW_MODE } from './app/runtime.js';
import { PanelStore } from './app/store.js';
import { applyTheme, readThemePreference } from './app/theme.js';
import type { DevTools } from './screens/Diger.js';

const runtime = chooseRuntime({
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  search: window.location.search,
});
setRouterMode(runtime.router);

const params = new URLSearchParams(window.location.search);
const themeParam = params.get('theme');
applyTheme(themeParam === 'dark' || themeParam === 'light' ? themeParam : readThemePreference());

// Hold tickets are in local monotonic time: the API client and the store use the same clock.
const monoNow = () => performance.now();

let api: TwinApi;
let live: LiveEvents;
let auth: AuthApi;
let watch: SessionWatch;
let clock: { now(): number };
let devTools: DevTools | null = null;
// Known at build time, so the production bundle leaves the mock out entirely.
const mockAllowed = import.meta.env.DEV || import.meta.env.MODE === PREVIEW_MODE;
if (runtime.dataSource === 'http' || !mockAllowed) {
  auth = createHttpAuthApi();
  const sessionWatch = createSessionWatch(auth);
  watch = sessionWatch;
  const stream = createEventSourceLive(`${API_BASE_PATH}/events`);
  // A stream that drops may have lost its session: ask the server (throttled) and end it on screen.
  live = {
    lastEventId: () => stream.lastEventId(),
    connect: (onEvent, onStatus) =>
      stream.connect(onEvent, (status) => {
        onStatus(status);
        if (status === 'error') sessionWatch.probe();
      }),
  };
  api = createHttpApi({
    viewId: () => stream.lastEventId(),
    localNow: monoNow,
    // 401 on any panel request: the session is gone (idle, absolute, logout elsewhere).
    fetch: watchingFetch(sessionWatch),
  });
  clock = { now: () => Date.now() };
} else {
  // Loaded only in development and the static preview: the production bundle has no mock.
  const [{ SEED_NOW }, { createScenarioClock, MockServer }, { createMockAuth }] = await Promise.all([
    import('./api/mock/seed.js'),
    import('./api/mock/server.js'),
    import('./api/mock/auth.js'),
  ]);
  clock = createScenarioClock(SEED_NOW, runtime.mockMode !== 'live');
  const server = new MockServer({ clock, mode: runtime.mockMode, latencyMs: 120, monoNow });
  api = server.api;
  live = server.live;
  auth = createMockAuth({ signedIn: params.get('auth') !== 'login', latencyMs: 300 });
  watch = createSessionWatch(auth);
  devTools = { setOnline: (online) => server.setOnline(online), online: () => server.isOnline() };
}
const createStore = () => new PanelStore({ api, live, clock, monoNow });

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <AuthGate auth={auth} watch={watch} createStore={createStore} devTools={devTools} />
  </StrictMode>,
);

// App shell only: the service worker never caches API data, so approvals are never shown from cache.
if (runtime.serviceWorker && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Without a service worker the panel still works; it just cannot start offline.
    });
  });
}
